"use client";
import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  subject?: string;
  message?: string;
}

export default function ContactForm() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'الرجاء إدخال الاسم الكامل';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'الرجاء إدخال البريد الإلكتروني';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'البريد الإلكتروني غير صحيح';
    }

    if (!formData.subject.trim()) {
      newErrors.subject = 'الرجاء إدخال موضوع الرسالة';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'الرجاء إدخال نص الرسالة';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'الرسالة قصيرة جداً (10 أحرف على الأقل)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));

    setIsSubmitting(false);
    setSubmitStatus('success');

    // Reset form
    setFormData({
      name: '',
      email: '',
      subject: '',
      message: '',
    });

    // Reset success message after 5 seconds
    setTimeout(() => {
      setSubmitStatus('idle');
    }, 5000);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error for this field
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  if (!isHydrated) {
    return (
      <section className="py-20 px-6 bg-gray-50" dir="rtl">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-lg p-12 animate-pulse">
            <div className="h-8 bg-gray-100 rounded mb-6"></div>
            <div className="space-y-4">
              <div className="h-12 bg-gray-100 rounded"></div>
              <div className="h-12 bg-gray-100 rounded"></div>
              <div className="h-32 bg-gray-100 rounded"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 px-6 bg-gray-50" dir="rtl">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4 font-cairo">
            نموذج التواصل
          </h2>
          <p className="text-slate-600 text-lg font-cairo">
            يمكنكم التواصل معنا مباشرة عبر النموذج التالي، وسيتم الرد على استفساراتكم في أقرب وقت ممكن.
          </p>
        </div>

        {/* Form Container */}
        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-lg p-8 md:p-12 shadow-sm"
        >
          {/* Success Message */}
          {submitStatus === 'success' && (
            <div className="mb-8 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
              <Icon name="CheckCircleIcon" size={24} className="text-green-600 flex-shrink-0" />
              <p className="text-green-700 font-cairo">
                تم إرسال رسالتك بنجاح! سنتواصل معك في أقرب وقت ممكن.
              </p>
            </div>
          )}

          {/* Name Field */}
          <div className="mb-6">
            <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2 font-cairo text-right">
              الاسم
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`w-full border ${
                errors.name ? 'border-red-300' : 'border-gray-200'
              } rounded-lg px-4 py-3 text-slate-900 focus:outline-none focus:border-slate-400 transition-colors placeholder-gray-400 font-cairo text-right`}
              placeholder="ادخل اسمك الكامل"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600 font-cairo text-right">{errors.name}</p>
            )}
          </div>

          {/* Email Field */}
          <div className="mb-6">
            <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2 font-cairo text-right">
              البريد الإلكتروني
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`w-full border ${
                errors.email ? 'border-red-300' : 'border-gray-200'
              } rounded-lg px-4 py-3 text-slate-900 focus:outline-none focus:border-slate-400 transition-colors placeholder-gray-400 text-right`}
              placeholder="example@email.com"
              dir="ltr"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600 font-cairo text-right">{errors.email}</p>
            )}
          </div>

          {/* Subject Field */}
          <div className="mb-6">
            <label htmlFor="subject" className="block text-sm font-medium text-slate-700 mb-2 font-cairo text-right">
              الموضوع
            </label>
            <input
              type="text"
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              className={`w-full border ${
                errors.subject ? 'border-red-300' : 'border-gray-200'
              } rounded-lg px-4 py-3 text-slate-900 focus:outline-none focus:border-slate-400 transition-colors placeholder-gray-400 font-cairo text-right`}
              placeholder="موضوع الرسالة"
            />
            {errors.subject && (
              <p className="mt-1 text-sm text-red-600 font-cairo text-right">{errors.subject}</p>
            )}
          </div>

          {/* Message Field */}
          <div className="mb-8">
            <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2 font-cairo text-right">
              الرسالة
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={6}
              className={`w-full border ${
                errors.message ? 'border-red-300' : 'border-gray-200'
              } rounded-lg px-4 py-3 text-slate-900 focus:outline-none focus:border-slate-400 transition-colors placeholder-gray-400 resize-none font-cairo text-right`}
              placeholder="اكتب رسالتك هنا..."
            ></textarea>
            {errors.message && (
              <p className="mt-1 text-sm text-red-600 font-cairo text-right">{errors.message}</p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full px-8 py-4 bg-primary text-white font-semibold rounded-lg hover:bg-primary/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 font-cairo"
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                جاري الإرسال...
              </>
            ) : (
              <>
                إرسال الرسالة
                <Icon name="PaperAirplaneIcon" size={20} />
              </>
            )}
          </button>
        </form>
      </div>
    </section>
  );
}